/**
 * IRIS : une bulle de savon. Transparente, presque vide, avec un bord irise qui tourne lentement
 * et une paroi qui tremble a peine.
 *
 * L'IDEE, EN UN MOT : fragile et precieux, joueur sans etre enfantin. C'est le seul look dont la
 * figure ne remplit rien : ce qu'on voit, c'est la page du client a travers elle.
 *
 * L'IRISATION EST LA SEULE COULEUR QUI NE VIENNE PAS DU CLIENT, et c'est physique : un film de
 * savon diffracte tout le spectre, pas une teinte. Elle est confinee au BORD par un masque
 * radial ; le centre reste vide, sinon la bulle devient une pastille.
 *
 * AU TEMPS FORT ELLE ECLATE ET SE REFORME. Trois quarts de seconde de rien entre les deux : ce
 * vide est le geste. A L'OUVERTURE elle se pose, cesse de trembler, et une croix de lumiere se
 * reflete dedans.
 *
 * LE SURVOL ARRETE LE TREMBLEMENT AVANT DE L'ETIRER. Une animation en cours ecrase la valeur
 * declaree de la meme propriete : sans couper le tremblement, l'etirement du survol ne se serait
 * jamais vu. Mesure faite, corrige ici, et c'est aussi la meilleure lecture — une bulle qu'on
 * approche se fige avant de se tendre.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */
export default {
  id: 'iris',

  css: `
/* LA PAROI. Presque tout le disque est vide ; la matiere se concentre sur les vingt derniers pour
   cent du rayon, la ou le film de savon est le plus epais. Chaque color-mix est precede de sa
   couleur de repli : sans elle, un navigateur qui l'ignore rendrait une bulle sans bord. */
.woot-lanceur[data-look="iris"] .woot-iris__bulle {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, .04);
  background: radial-gradient(circle at 50% 42%, rgba(255, 255, 255, .03) 48%, color-mix(in srgb, var(--client) 14%, transparent) 72%, color-mix(in srgb, var(--client) 34%, transparent) 90%, color-mix(in srgb, var(--client) 18%, transparent) 100%);
  border: 1px solid rgba(255, 255, 255, .6);
  border: 1px solid color-mix(in srgb, var(--client) 58%, white);
  box-shadow: 0 10px 26px rgba(16, 19, 23, .18), inset 0 0 14px rgba(255, 255, 255, .3);
  box-shadow: 0 10px 26px color-mix(in srgb, var(--client) 26%, transparent), inset 0 -6px 14px color-mix(in srgb, var(--client) 22%, transparent), inset 0 0 14px rgba(255, 255, 255, .3);
  transition: transform .45s cubic-bezier(.34, 1.56, .64, 1), opacity .3s ease;
}

/* LE TREMBLEMENT DEFORME LE RAYON, PAS SEULEMENT L'ECHELLE : une bulle de savon n'est jamais tout
   a fait ronde, et c'est ce qui la distingue d'un ballon. */
.woot-lanceur[data-look="iris"][data-etat="repos"] .woot-iris__bulle {
  animation: woot-iris-tremble 5s ease-in-out infinite;
}

@keyframes woot-iris-tremble {
  0%, 100% { border-radius: 50%; transform: scale(1, 1); }
  25% { border-radius: 52% 48% 50% 50% / 48% 52% 48% 52%; transform: scale(1.03, .97); }
  50% { transform: scale(.98, 1.03); }
  75% { border-radius: 48% 52% 50% 50% / 52% 48% 52% 48%; transform: scale(1.02, .99); }
}

/* L'IRISATION : tout le spectre, masque de sorte a n'exister qu'au bord. Le prefixe webkit sert
   les navigateurs ou mask n'est pas encore sans prefixe ; sans lui la couronne remplit le disque
   au lieu de le border. */
.woot-lanceur[data-look="iris"] .woot-iris__irisation {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: conic-gradient(from 0deg, #ff9ad9, #9dffdc, #9cc3ff, #ffe89a, #ff9ad9);
  opacity: .55;
  -webkit-mask: radial-gradient(circle, transparent 56%, rgba(0, 0, 0, .9) 66%, rgba(0, 0, 0, .35) 100%);
  mask: radial-gradient(circle, transparent 56%, rgba(0, 0, 0, .9) 66%, rgba(0, 0, 0, .35) 100%);
}

.woot-lanceur[data-look="iris"][data-etat="repos"] .woot-iris__irisation,
.woot-lanceur[data-look="iris"][data-etat="attention"] .woot-iris__irisation {
  animation: woot-iris-tourne 12s linear infinite;
}

@keyframes woot-iris-tourne { to { transform: rotate(360deg); } }

/* Les deux reflets : le grand en haut a gauche dit ou est la lumiere, le petit en bas a droite
   dit que la bulle est un volume et non un cercle. */
.woot-lanceur[data-look="iris"] .woot-iris__reflet {
  position: absolute;
  left: 12px;
  top: 9px;
  width: 20px;
  height: 11px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .88);
  filter: blur(1.5px);
  transform: rotate(-25deg);
}

.woot-lanceur[data-look="iris"] .woot-iris__reflet--bas {
  left: auto;
  top: auto;
  right: 12px;
  bottom: 10px;
  width: 14px;
  height: 7px;
  background: rgba(255, 255, 255, .35);
  filter: blur(2px);
  transform: none;
}

/* LE TEMPS FORT : elle gonfle, creve, et il ne reste rien pendant presque une seconde avant
   qu'elle ne se reforme. */
.woot-lanceur[data-look="iris"][data-etat="attention"] .woot-iris__bulle {
  animation: woot-iris-eclate 1.7s cubic-bezier(.2, .8, .2, 1);
}

@keyframes woot-iris-eclate {
  0% { transform: scale(1); opacity: 1; }
  18% { transform: scale(1.18); opacity: 1; }
  22% { transform: scale(1.26); opacity: 0; }
  40% { transform: scale(.2); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

/* AU SURVOL elle s'immobilise puis s'etire vers le pointeur. Le selecteur porte [data-etat] pour
   peser autant que la regle de tremblement au-dessus : sans ce poids, son raccourci animation
   gagnerait et l'etirement ne s'appliquerait jamais. */
.woot-lanceur[data-look="iris"][data-etat]:hover .woot-iris__bulle {
  animation: none;
  transform: scale(1.06) translate(-2px, -2px);
}

/* L'ACCENT : la bulle de dialogue qui sort au temps fort, en haut a gauche du lanceur, comme sur
   les autres looks a disque. */
.woot-lanceur[data-look="iris"] .woot-iris__accent {
  position: absolute;
  left: calc(100% - 16px);
  bottom: calc(100% - 16px);
  opacity: 0;
  transform: scale(.4);
  transform-origin: bottom left;
  transition: transform .4s cubic-bezier(.34, 1.56, .64, 1), opacity .25s ease;
}

.woot-lanceur[data-look="iris"][data-etat="attention"] .woot-iris__accent { opacity: 1; transform: scale(1); }

/* OUVERT : elle se pose, cesse de trembler, et la croix de lumiere se reflete dedans. Sa couleur
   passe par le fond des deux barres et non par --woot-croix : LA BULLE EST TRANSPARENTE, donc la
   croix se lit sur la page du client et non sur un disque. Elle prend la couleur de l'inbox tant
   que celle-ci s'y lit, et l'encre du theme quand elle est trop pale — un blanc melange a la
   couleur y rendait une croix a 1,2:1. Le second argument du var() est la chaine de repli. */
.woot-lanceur[data-look="iris"] .woot-lanceur__croix {
  width: 22px;
  height: 22px;
  margin: -11px;
}

.woot-lanceur[data-look="iris"] .woot-lanceur__croix::before,
.woot-lanceur[data-look="iris"] .woot-lanceur__croix::after {
  height: 1.8px;
  background: var(--woot-encre-auto, var(--client));
  box-shadow: 0 0 6px rgba(255, 255, 255, .75);
}

.woot-lanceur[data-look="iris"][data-etat="ouvert"] .woot-iris__bulle {
  animation: none;
  transform: scale(.94);
}
`,

  monter: conteneur => {
    const bulle = document.createElement('div');
    bulle.className = 'woot-iris__bulle';
    const irisation = document.createElement('span');
    irisation.className = 'woot-iris__irisation';
    const reflet = document.createElement('span');
    reflet.className = 'woot-iris__reflet';
    const refletBas = document.createElement('span');
    refletBas.className = 'woot-iris__reflet woot-iris__reflet--bas';
    bulle.appendChild(irisation);
    bulle.appendChild(reflet);
    bulle.appendChild(refletBas);

    const croix = document.createElement('span');
    croix.className = 'woot-lanceur__croix';

    const accent = document.createElement('div');
    accent.className = 'woot-iris__accent';
    accent.innerHTML =
      '<svg viewBox="0 0 28 24" width="28" height="24" aria-hidden="true">' +
      '<path d="M5 2h18a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9l-6 5v-5H5a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" fill="#fff" stroke="rgba(0,0,0,.12)"/>' +
      '<g fill="var(--client)"><circle cx="9" cy="10.5" r="1.6"/><circle cx="14" cy="10.5" r="1.6"/>' +
      '<circle cx="19" cy="10.5" r="1.6"/></g></svg>';

    conteneur.appendChild(bulle);
    conteneur.appendChild(croix);
    conteneur.appendChild(accent);
  },

  // IRIS EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous les
  // looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et il n'y a rien a mettre en pause que la feuille n'arrete deja.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
